import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import RightPanel from '../../RightPanel'
import Loading from '../../../molecules/Loading'
import ErrorDisplay from '../../../molecules/ErrorDisplay'
import SydTabs from '../../../commonDesign/SydTabs'
import { dataPipelines } from '../../../../api/coreData'
import InspectorJsonView from './components/InspectorJsonView'
import OrionConnectTab from './tabs/OrionConnectTab'
import AccessorsTab from './tabs/AccessorsTab'
import StepsTab from './tabs/StepsTab'

const DRAWER_WIDTH = 620
const DEFAULT_TAB = 'orion'

const TAB_OPTIONS = {
  orion: { label: 'Orion connect', order: 1 },
  accessors: { label: 'Accessors', order: 2 },
  pipeline: { label: 'Pipeline', order: 3 },
  variables: { label: 'Variables', order: 4 },
  steps: { label: 'Steps', order: 5 }
}

const useStyles = makeStyles(() => ({
  meta: {
    padding: '12px 0',
    borderBottom: '1px solid var(--color-neutral-200)',
    fontSize: 12,
    color: 'var(--color-neutral-600)',
    columnGap: 12,
    rowGap: 4,
    gridTemplateColumns: 'auto 1fr'
  },
  metaKey: { color: 'var(--color-neutral-500)' },
  metaVal: { color: 'var(--color-neutral-800)', wordBreak: 'break-all' },
  // The MUI Tabs default size is too tall for the dense inspector aesthetic;
  // override down to 32px and shrink the labels so the strip reads as compact
  // filter chips.
  tabs: {
    '& .MuiTab-root': {
      fontSize: 10,
      minWidth: 'auto',
      minHeight: 32,
      padding: '4px 10px',
      letterSpacing: '0.4px',
      textTransform: 'uppercase'
    },
    '& .MuiTabs-root': {
      minHeight: 32
    }
  }
}))

export default function DDPInspectorDrawer ({ entry, onClose }) {
  const classes = useStyles()
  const open = !!entry

  const debugRequest = useMemo(() => {
    if (!entry) return null
    const isCode = typeof entry.pipeline === 'string'
    return isCode
      ? { pipelineCode: entry.pipeline, variables: entry.variables || {} }
      : { pipeline: entry.pipeline, variables: entry.variables || {} }
  }, [entry])

  const { data, isLoading, error } = dataPipelines.useDebugDataPipeline(
    debugRequest || {},
    { enabled: open && !!debugRequest }
  )

  const builtOrionRequests = useMemo(
    () => (data?.steps || [])
      .filter(s => s.orionConnect && !s.orionConnect.skipped)
      .map(s => s.orionConnect.body),
    [data]
  )
  const reportingCallCount = builtOrionRequests.length

  return (
    <RightPanel
      open={open}
      toggleOpen={onClose}
      title={<span>DDP Inspector{entry ? ` — ${entry.displayName}` : ''}</span>}
      width={`${DRAWER_WIDTH}px`}
      height='100%'
      showCloseButton
    >
      <div className='flex flex-col h-full min-h-0 px-4 pb-4'>
        {!entry ? null : isLoading ? (
          <Loading />
        ) : error ? (
          <ErrorDisplay error={error} />
        ) : !data ? null : (
          <>
            <div className={`${classes.meta} font-mono grid flex-shrink-0`}>
              <span className={classes.metaKey}>pipelineCode</span>
              <span className={classes.metaVal}>{data.pipelineCode || '(inline)'}</span>
              <span className={classes.metaKey}>steps</span>
              <span className={classes.metaVal}>{data.steps?.length ?? 0}</span>
              <span className={classes.metaKey}>orion reporting calls</span>
              <span className={classes.metaVal}>{reportingCallCount}</span>
            </div>

            <div className={`${classes.tabs} flex flex-col flex-1 min-h-0 mt-3`}>
              <SydTabs.Group tabOptions={TAB_OPTIONS} defaultValue={DEFAULT_TAB}>
                <SydTabs.List />

                <SydTabs.Tab value='orion'>
                  <div className='flex flex-col flex-1 min-h-0 overflow-hidden mt-3'>
                    <OrionConnectTab steps={data.steps} entry={entry} />
                  </div>
                </SydTabs.Tab>

                <SydTabs.Tab value='accessors'>
                  <div className='flex flex-col flex-1 min-h-0 overflow-hidden mt-3'>
                    <div className='flex-1 min-h-0 overflow-auto'>
                      <AccessorsTab entry={entry} orionRequests={builtOrionRequests} />
                    </div>
                  </div>
                </SydTabs.Tab>

                <SydTabs.Tab value='pipeline'>
                  <div className='flex flex-col flex-1 min-h-0 overflow-hidden mt-3'>
                    <InspectorJsonView
                      value={data.resolvedPipeline}
                      headerLabel='Resolved pipeline definition'
                      copyLabel='Copy JSON'
                      name='ddp-inspector-pipeline'
                    />
                  </div>
                </SydTabs.Tab>

                <SydTabs.Tab value='variables'>
                  <div className='flex flex-col flex-1 min-h-0 overflow-hidden mt-3'>
                    <InspectorJsonView
                      value={data.variables}
                      headerLabel='Variables (after wrapping for execute-data-pipeline)'
                      copyLabel='Copy JSON'
                      name='ddp-inspector-variables'
                    />
                  </div>
                </SydTabs.Tab>

                <SydTabs.Tab value='steps'>
                  <div className='flex flex-col flex-1 min-h-0 overflow-hidden mt-3'>
                    <StepsTab steps={data.steps} />
                  </div>
                </SydTabs.Tab>
              </SydTabs.Group>
            </div>
          </>
        )}
      </div>
    </RightPanel>
  )
}

DDPInspectorDrawer.propTypes = {
  entry: PropTypes.object,
  onClose: PropTypes.func.isRequired
}
